import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import Alert from '@amzn/awsui-components-console/alert';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import FormField from '@amzn/awsui-components-console/form-field';
import Modal from '@amzn/awsui-components-console/modal';
import Select, { SelectProps } from '@amzn/awsui-components-console/select';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Textarea from '@amzn/awsui-components-console/textarea';
import {
  persistFieldEdit,
  StringFieldEdit,
} from 'src/features/string-group-detail/utils/draftStorage';
import {
  CONTEXT_NOTE_CHAR_LIMIT,
  SOURCE_TEXT_CHAR_LIMIT,
} from 'src/features/string-group-detail/constants';
import {
  buildServerSnapshot,
  EditDraftContext,
  parseBaseVersion,
} from '../utils/editDraft';

interface CreateVariantModalProps {
  visible: boolean;
  stringId: string;
  /** CX IDs with no variant yet — server list plus the draft's pending ones. */
  availableCxIds: string[];
  /** Base source text, prefilled as the variant's starting text. */
  baseSourceText: string;
  /** Draft identity plus the base snapshot the change is written onto. */
  draftContext: EditDraftContext;
  onDismiss: () => void;
  /** Lets the page re-read the draft it just wrote. */
  onDraftSaved?: () => void;
}

/**
 * Creates a CX variant by writing it into the group draft.
 *
 * There is no create-variant mutation: a new variant is the same draft write as
 * a variant edit, with the CX ID chosen rather than fixed (`upsertVariant`
 * appends when the CX ID is new). So saving redirects to the group page, where
 * the user reviews the pending change and submits the group.
 *
 * Inline markup and message format are deliberately NOT offered. They are
 * string-level in TRANSCOM — a variant never overrides them — so the change
 * omits both keys and `applyFieldEdit` leaves the entry's format exactly as it
 * was, including leaving it absent for an UNKNOWN format.
 */
const CreateVariantModal: React.FC<CreateVariantModalProps> = ({
  visible,
  stringId,
  availableCxIds,
  baseSourceText,
  draftContext,
  onDismiss,
  onDraftSaved,
}) => {
  const navigate = useNavigate();

  const [selectedCxId, setSelectedCxId] = useState<SelectProps.Option | null>(
    null,
  );
  const [sourceText, setSourceText] = useState('');
  const [contextNote, setContextNote] = useState('');
  /**
   * Why the last save attempt did not persist. While set, the modal stays open
   * with its content so the user can retry or copy the text out.
   */
  const [saveError, setSaveError] = useState<string | undefined>(undefined);

  // Seed on open rather than on mount: the variant starts as a copy of the base
  // text, and a previous attempt's content or error must not greet the next open.
  useEffect(() => {
    if (visible) {
      setSelectedCxId(null);
      setSourceText(baseSourceText);
      setContextNote('');
      setSaveError(undefined);
    }
  }, [visible, baseSourceText]);

  const trimmedSourceText = sourceText.trim();
  const trimmedContextNote = contextNote.trim();

  // Source text is required; the context note is optional (the mock opens it
  // empty with a placeholder). Both are capped at the shared limits.
  const sourceTextError = !trimmedSourceText
    ? 'Variant source text is required.'
    : trimmedSourceText.length > SOURCE_TEXT_CHAR_LIMIT
      ? `Variant source text exceeds ${SOURCE_TEXT_CHAR_LIMIT} character limit.`
      : undefined;
  const contextNoteError =
    trimmedContextNote.length > CONTEXT_NOTE_CHAR_LIMIT
      ? `Context note exceeds ${CONTEXT_NOTE_CHAR_LIMIT} character limit.`
      : undefined;

  const isSaveDisabled =
    !selectedCxId || !!sourceTextError || !!contextNoteError;

  const handleSave = (cxId: string) => {
    // Only the one variant this modal authored is expressed, so an existing
    // draft entry keeps its other pending values. No format keys — see the
    // component doc.
    const change: StringFieldEdit = {
      kind: 'variant',
      cxId,
      sourceText: trimmedSourceText,
      contextNote: trimmedContextNote,
    };

    try {
      persistFieldEdit(
        draftContext.namespaceId,
        draftContext.stringGroupId,
        parseBaseVersion(draftContext.stringGroupVersion),
        buildServerSnapshot(draftContext),
        change,
      );
    } catch (err) {
      // The draft is the ONLY copy of this variant, so a rejected write must not
      // close the modal or navigate.
      setSaveError(
        err instanceof Error && err.message
          ? err.message
          : 'Unknown storage error.',
      );
      return;
    }

    setSaveError(undefined);
    onDraftSaved?.();
    onDismiss();
    navigate(
      `/${encodeURIComponent(draftContext.namespaceId)}/${encodeURIComponent(draftContext.stringGroupId)}`,
    );
  };

  if (!visible) {
    return null;
  }

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header={`Create variant for ${stringId}`}
      footer={
        <Box float="right">
          <SpaceBetween size="xs" direction="horizontal">
            <Button
              variant="link"
              onClick={onDismiss}
              data-testid="create-variant-cancel-button"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              // The CX ID re-check is TypeScript narrowing only — Save is
              // disabled until one is selected.
              onClick={() => {
                if (selectedCxId?.value) {
                  handleSave(selectedCxId.value);
                }
              }}
              disabled={isSaveDisabled}
              data-testid="create-variant-save-button"
            >
              Save changes
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <div data-testid="create-variant-modal">
        <SpaceBetween size="m">
          {saveError && (
            <Alert
              type="error"
              header="Changes not saved"
              data-testid="create-variant-save-error"
            >
              {`Your changes could not be saved to this browser: ${saveError} Nothing has been discarded — copy your text elsewhere, free up browser storage, or try again.`}
            </Alert>
          )}
          <Alert type="warning" data-testid="create-variant-warning">
            Variants should always maintain the same semantic meaning of the
            base string.
          </Alert>
          <FormField label="CX ID *" constraintText="Choose one.">
            <Select
              selectedOption={selectedCxId}
              placeholder="Select from available CX IDs"
              options={availableCxIds.map((cxId) => ({
                label: cxId,
                value: cxId,
              }))}
              onChange={({ detail }) => setSelectedCxId(detail.selectedOption)}
              data-testid="create-variant-cxid-select"
            />
          </FormField>
          <FormField
            label="Variant source text *"
            errorText={sourceTextError}
            constraintText={`Character limit: ${SOURCE_TEXT_CHAR_LIMIT.toLocaleString()}`}
          >
            <Textarea
              value={sourceText}
              onChange={({ detail }) => setSourceText(detail.value)}
              rows={4}
              data-testid="create-variant-source-text"
            />
          </FormField>
          <FormField
            label="Context note"
            errorText={contextNoteError}
            constraintText={`Character limit: ${CONTEXT_NOTE_CHAR_LIMIT.toLocaleString()}`}
          >
            <Textarea
              value={contextNote}
              onChange={({ detail }) => setContextNote(detail.value)}
              placeholder="Provide context for translators."
              rows={3}
              data-testid="create-variant-context-note"
            />
          </FormField>
        </SpaceBetween>
      </div>
    </Modal>
  );
};

export default CreateVariantModal;
